import { useMemo, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  Send,
  XCircle,
  MessageSquare,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { useProject, useDevelopers, useInviteDeveloper, useCancelProject } from '@/hooks'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Textarea } from '@/components/ui'
import { Skeleton } from '@/components/ui/skeleton'
import { UserAvatar } from '@/components/UserAvatar'

function StatusBadge({ status }: { status: string }) {
  const label = status.replace(/_/g, ' ')
  const variant = (() => {
    switch (status) {
      case 'open':
        return 'secondary'
      case 'in_progress':
        return 'default'
      case 'completed':
        return 'outline'
      case 'cancelled':
        return 'destructive'
      default:
        return 'secondary'
    }
  })()

  return (
    <Badge variant={variant} className="uppercase tracking-wide">
      {label}
    </Badge>
  )
}

function ProjectTypeBadge({ type }: { type: 'private' | 'public' }) {
  return (
    <Badge variant={type === 'public' ? 'default' : 'secondary'} className="capitalize">
      {type}
    </Badge>
  )
}

function formatDateTime(input: string) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }).format(new Date(input))
  } catch (error) {
    return input
  }
}

export default function ProjectDetail() {
  const { user } = useAuth()
  const params = useParams<{ projectId: string }>()
  const projectId = params.projectId ? Number(params.projectId) : NaN

  const [inviteMessage, setInviteMessage] = useState('')

  const projectQuery = useProject(Number.isFinite(projectId) ? projectId : null)
  const developersQuery = useDevelopers()
  const inviteDeveloper = useInviteDeveloper()
  const cancelProject = useCancelProject()

  const project = projectQuery.data
  const userAccountIds = useMemo(
    () => user?.accounts?.map((account) => account.account_id) ?? [],
    [user]
  )
  const canManage = Boolean(
    project &&
      user &&
      (project.created_by === user.id || userAccountIds.includes(project.account_id))
  )

  if (!Number.isFinite(projectId)) {
    return <Navigate to="/projects" replace />
  }

  if (projectQuery.isLoading) {
    return (
      <div className="container mx-auto max-w-5xl space-y-6 px-4 py-10">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-16 w-full" />
        <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    )
  }

  if (projectQuery.error) {
    return (
      <div className="container mx-auto max-w-4xl space-y-4 px-4 py-16 text-center">
        <AlertTriangle className="mx-auto h-10 w-10 text-destructive" />
        <p className="text-lg font-semibold">We could not load this project.</p>
        <p className="text-sm text-muted-foreground">
          It may have been removed or you no longer have access.
        </p>
        <Button asChild className="mt-4">
          <Link to="/projects">Back to projects</Link>
        </Button>
      </div>
    )
  }

  if (!project) {
    return <Navigate to="/projects" replace />
  }
  const invitePendingDeveloperId = inviteDeveloper.variables?.developerId
  const invitedDevelopers = new Set(
    project.invitations.map((invitation) => invitation.developer_id)
  )
  const isCancelPending = cancelProject.isPending && cancelProject.variables === project.id

  const handleInvite = (developerId: number) => {
    inviteDeveloper.mutate(
      {
        projectId: project.id,
        developerId,
        message: inviteMessage.trim() || undefined,
      },
      {
        onSuccess: () => {
          setInviteMessage('')
        },
      }
    )
  }

  return (
    <div className="container mx-auto max-w-6xl space-y-8 px-4 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Button asChild variant="ghost" size="sm" className="gap-2 pl-0">
          <Link to="/projects">
            <ChevronLeft className="h-4 w-4" />
            Back to projects
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={project.status} />
          <ProjectTypeBadge type={project.project_type} />
        </div>
      </div>

      <header className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">{project.title}</h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <UserAvatar
              size="sm"
              avatarUrl={project.created_by_avatar_url}
              username={project.created_by_username ?? undefined}
            />
            <span>
              Created by{' '}
              {project.created_by_username ? (
                <Link
                  to={`/profiles/${project.created_by_username}`}
                  className="text-primary hover:underline"
                >
                  {project.created_by_username}
                </Link>
              ) : (
                'Unknown'
              )}
            </span>
          </div>
          <span>•</span>
          <div className="flex items-center gap-2">
            <UserAvatar
              size="sm"
              avatarUrl={project.account_avatar_url}
              name={project.account_name}
            />
            <span>{project.account_name}</span>
          </div>
          <span>•</span>
          <span>
            Posted {formatDateTime(project.created_at)} &middot; Updated{' '}
            {formatDateTime(project.updated_at)}
          </span>
        </div>
      </header>

      <div className="grid gap-8 lg:grid-cols-[2fr,1fr]">
        <section className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Project overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm leading-relaxed text-muted-foreground">
              <p>{project.description}</p>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border border-muted p-4">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Budget
                  </h3>
                  {project.budget_min || project.budget_max ? (
                    <p>
                      {project.budget_min ? `from $${project.budget_min}` : 'Flexible budget'}{' '}
                      {project.budget_max ? `to $${project.budget_max}` : ''}
                    </p>
                  ) : (
                    <p>Not specified</p>
                  )}
                </div>
                <div className="rounded-lg border border-muted p-4">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Skills
                  </h3>
                  {project.skills.length ? (
                    <div className="flex flex-wrap gap-2">
                      {project.skills.map((skill) => (
                        <Badge key={skill.id} variant="outline" className="text-xs">
                          {skill.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p>No skills provided</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Proposals</CardTitle>
            </CardHeader>
            <CardContent>
              {project.proposals.length === 0 ? (
                <p className="text-sm text-muted-foreground">No proposals submitted yet.</p>
              ) : (
                <div className="space-y-4">
                  {project.proposals.map((proposal) => (
                    <div key={proposal.id} className="rounded-lg border border-muted/60 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <UserAvatar
                            size="sm"
                            avatarUrl={proposal.submitted_by_avatar_url}
                            username={proposal.submitted_by_username}
                          />
                          <div>
                            <p className="text-sm font-semibold">
                              {proposal.submitted_by_username}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Submitted {formatDateTime(proposal.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {canManage && (
                            <Button size="sm" variant="ghost" asChild>
                              <Link
                                to={`/messages?user=${proposal.submitted_by_username}&project=${project.id}`}
                              >
                                <MessageSquare className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {proposal.accepted && (
                            <Badge variant="default" className="gap-1">
                              <CheckCircle2 className="h-3.5 w-3.5" /> Accepted
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 text-sm leading-relaxed text-muted-foreground">
                        {proposal.cover_letter}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
                        {proposal.hourly_rate && <span>Hourly: ${proposal.hourly_rate}</span>}
                        {proposal.fixed_price && <span>Fixed: ${proposal.fixed_price}</span>}
                        <span>Account: {proposal.account_name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Invitations</CardTitle>
            </CardHeader>
            <CardContent>
              {project.invitations.length === 0 ? (
                <p className="text-sm text-muted-foreground">No invitations have been sent yet.</p>
              ) : (
                <div className="space-y-4">
                  {project.invitations.map((invitation) => (
                    <div key={invitation.id} className="rounded-lg border border-muted/60 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <UserAvatar
                            size="sm"
                            avatarUrl={invitation.developer_profile?.avatar_url}
                            username={invitation.developer_username}
                          />
                          <div>
                            <p className="text-sm font-semibold">
                              {invitation.developer_profile?.company ||
                                invitation.developer_username}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Sent {formatDateTime(invitation.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {canManage && (
                            <Button size="sm" variant="ghost" asChild>
                              <Link
                                to={`/messages?user=${invitation.developer_username}&project=${project.id}`}
                              >
                                <MessageSquare className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          <Badge variant="outline" className="capitalize">
                            {invitation.status}
                          </Badge>
                        </div>
                      </div>
                      {invitation.message && (
                        <p className="mt-3 text-sm text-muted-foreground">“{invitation.message}”</p>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                        {invitation.developer_profile?.skills?.map((skill) => (
                          <Badge key={skill.id} variant="outline" className="text-[11px]">
                            {skill.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        <aside className="space-y-6">
          {canManage && (
            <Card>
              <CardHeader>
                <CardTitle>Project controls</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <p>
                  Cancel the project if you no longer need proposals. This action is only available
                  while the project is open and has no accepted proposals.
                </p>
                <Button
                  variant="outline"
                  className="gap-2"
                  disabled={!project.can_cancel || isCancelPending}
                  onClick={() => cancelProject.mutate(project.id)}
                >
                  <XCircle className="h-4 w-4" />
                  {isCancelPending
                    ? 'Cancelling…'
                    : project.can_cancel
                      ? 'Cancel project'
                      : 'Cannot cancel'}
                </Button>
              </CardContent>
            </Card>
          )}

          {canManage && (
            <Card>
              <CardHeader>
                <CardTitle>Invite developers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Invite message (optional)
                  </label>
                  <Textarea
                    value={inviteMessage}
                    onChange={(event) => setInviteMessage(event.target.value)}
                    placeholder="Share context or expectations you'd like developers to see."
                    rows={3}
                  />
                </div>
                <div className="space-y-3">
                  {developersQuery.isLoading && <Skeleton className="h-32 w-full" />}
                  {developersQuery.error && (
                    <p className="text-sm text-destructive">Unable to load developers right now.</p>
                  )}
                  {developersQuery.data?.slice(0, 10).map((developer) => {
                    const alreadyInvited = invitedDevelopers.has(developer.id)
                    const isSending =
                      inviteDeveloper.isPending && invitePendingDeveloperId === developer.id

                    return (
                      <div
                        key={developer.id}
                        className="rounded-lg border border-muted/70 p-3 text-sm"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="font-semibold">{developer.username}</p>
                            {developer.bio && (
                              <p className="line-clamp-2 text-xs text-muted-foreground">
                                {developer.bio}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {alreadyInvited ? (
                              <Badge variant="outline">Invited</Badge>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1"
                                disabled={alreadyInvited || isSending}
                                onClick={() => handleInvite(developer.id)}
                              >
                                <Send className="h-3.5 w-3.5" />
                                {isSending ? 'Sending…' : 'Send invite'}
                              </Button>
                            )}
                          </div>
                        </div>
                        {developer.skills?.length ? (
                          <div className="mt-2 flex flex-wrap gap-2 text-xs">
                            {developer.skills.map((skill) => (
                              <Badge key={skill.id} variant="outline">
                                {skill.name}
                              </Badge>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {!canManage && project.project_type === 'public' && (
            <Card>
              <CardHeader>
                <CardTitle>Ready to collaborate?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>
                  Review the project details and submit a proposal outlining your approach, rates,
                  and relevant experience.
                </p>
                <p className="text-xs">
                  Proposal submission tooling will be available in a future update. For now,
                  coordinate with the client through the channel they provided.
                </p>
              </CardContent>
            </Card>
          )}
        </aside>
      </div>
    </div>
  )
}
